import { FinalFormAutocomplete } from "@comet/admin";
import { type ComponentType } from "react";
import { type FieldRenderProps } from "react-final-form";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PrimitiveValue = any;

export type PrimitiveAutocompleteProps = {
    options: readonly PrimitiveValue[];
    getOptionLabel?: (value: PrimitiveValue) => string;
    isOptionEqualToValue?: (option: PrimitiveValue, value: PrimitiveValue) => boolean;
    clearable?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [prop: string]: any;
} & Partial<FieldRenderProps<PrimitiveValue, HTMLInputElement | HTMLTextAreaElement>>;

// Comet's FinalFormAutocomplete is typed for object options (`T extends Record<string, any>`), but
// MUI's Autocomplete renders primitive (string/number) options natively and Comet stores the selected
// option as-is via `onChange(option)`. Feeding bare primitive values therefore keeps the field value
// the scalar/enum the GraphQL API expects (the app-wide contract) — every filter/form here sends the
// field value straight into a GraphQL variable. We narrow the type so primitive options type-check.
//
// NOTE: Comet coerces falsy field values to "nothing selected" (`value ? value : null` in its
// Autocomplete, and its default `isOptionEqualToValue` bails on falsy `value`). This component is
// therefore only safe for non-empty string options; boolean/number-zero selects must keep using
// FinalFormSelect (see StaticSelectField's `searchable={false}` path).
const TypedFinalFormAutocomplete = FinalFormAutocomplete as unknown as ComponentType<PrimitiveAutocompleteProps>;

export const PrimitiveAutocomplete = ({ isOptionEqualToValue, placeholder, input, ...props }: PrimitiveAutocompleteProps) => {
    // FinalFormAutocomplete/MUI Autocomplete has no `placeholder` prop; passing one at the top level
    // would leak onto the root DOM node. But Comet spreads the final-form `input` object onto the inner
    // InputBase (which forwards `placeholder` to the native <input>), so injecting it there shows the
    // placeholder while empty and hides it once an option is selected — the same behavior the
    // FinalFormSelect path had. `input` is always supplied by the Field wrapper at runtime.
    const inputWithPlaceholder = (placeholder != null ? { ...input, placeholder } : input) as PrimitiveAutocompleteProps["input"];

    return (
        <TypedFinalFormAutocomplete
            input={inputWithPlaceholder}
            isOptionEqualToValue={isOptionEqualToValue ?? ((option, value) => String(option) === String(value))}
            {...props}
        />
    );
};
